import { useState, useEffect } from "react";
import { api } from "@/services/api";
import { Alert } from "@/components/shared";
import { type VerificationAction, type PPRAStatus, PPRAStatusLabels } from "@/types";

interface AuditEntry {
  id: string;
  admin_id: string;
  action: VerificationAction;
  previous_status: PPRAStatus;
  new_status: PPRAStatus;
  reason: string | null;
  created_at: string;
}

interface AuditLogViewerProps {
  agentId: string;
}

const actionLabels: Record<string, string> = {
  approve: "Approved",
  reject: "Rejected",
  revoke: "Revoked",
  expire: "Expired",
};

export function AuditLogViewer({ agentId }: AuditLogViewerProps) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [agentName, setAgentName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/management/audit-log/${agentId}`) as any;
        setEntries(res.data);
        setAgentName(res.agent_name);
      } catch (e: any) {
        setError(e.message || "Failed to load audit log");
      } finally {
        setLoading(false);
      }
    })();
  }, [agentId]);

  if (loading) return <p className="text-white/60">Loading audit log…</p>;
  if (error) return <Alert type="error">{error}</Alert>;

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-white/60">
        Audit Trail — {agentName}
      </h4>

      {entries.length === 0 ? (
        <p className="text-white/40 text-sm">No verification actions recorded.</p>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="border border-white/10 rounded-lg p-3 text-sm"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-white/80">
                  {actionLabels[entry.action] || entry.action}
                </span>
                <span className="text-white/40 text-xs">
                  {new Date(entry.created_at).toLocaleString()}
                </span>
              </div>
              <div className="text-white/50 text-xs mt-1">
                {PPRAStatusLabels[entry.previous_status] || entry.previous_status} →{" "}
                {PPRAStatusLabels[entry.new_status] || entry.new_status}
              </div>
              {entry.reason && (
                <p className="text-white/60 text-xs mt-1 italic">
                  "{entry.reason}"
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
